import type { ComponentProps, ReactNode } from "react";
import { Select as RadixSelectPrimitive } from "radix-ui";
import { tv } from "tailwind-variants";

import { Spinner } from "@/shared/ui/common";
import { inputStyles } from "@/shared/ui/form/input";
import { AltArrowDownIcon, AltArrowUpIcon, CheckIcon } from "@/shared/ui/icons";

export const selectStyles = tv({
  extend: inputStyles,
  slots: {
    root: "flex items-center gap-2 [&>[data-slot=right-icon]]:ml-auto data-[state=open]:[&>[data-slot=right-icon]]:rotate-180 [&>svg]:size-4",
    scrollButton: "flex items-center justify-center py-2",
    content:
      "relative z-50 max-h-[calc(var(--radix-select-content-available-height)-2rem)] min-w-(--radix-popover-trigger-width) overflow-x-hidden overflow-y-auto rounded-lg border bg-white text-neutral-950 shadow-md outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
    viewport: "flex flex-col gap-1 p-4",
    item: "relative flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-transparent p-2 text-sm outline-hidden select-none hover:bg-primary-50 focus:bg-primary-50 has-[[data-slot=item-indicator]]:border-primary-100 has-[[data-slot=item-indicator]]:bg-primary-50 data-disabled:pointer-events-none data-disabled:opacity-50",
  },
  variants: {
    position: {
      popper: {
        content:
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        viewport:
          "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1",
      },
      "item-aligned": "",
    },
  },
});

const { root, scrollButton, content, viewport, item } = selectStyles();

const Root = RadixSelectPrimitive.Root;

const Group = RadixSelectPrimitive.Group;

const Value = RadixSelectPrimitive.Value;

type TriggerProps = ComponentProps<typeof RadixSelectPrimitive.Trigger> & {
  isLoading?: boolean;
  leftIcon?: ReactNode;
};

const Trigger = ({
  className,
  children,
  isLoading,
  leftIcon,
  ...props
}: TriggerProps) => (
  <RadixSelectPrimitive.Trigger
    className={root({ className })}
    disabled={isLoading}
    {...props}
  >
    {leftIcon && (
      <RadixSelectPrimitive.Icon asChild className="text-secondary-400">
        {leftIcon}
      </RadixSelectPrimitive.Icon>
    )}

    {children}

    <RadixSelectPrimitive.Icon data-slot="right-icon" asChild>
      {isLoading ? (
        <Spinner className="fill-neutral-500 stroke-neutral-200" />
      ) : (
        <AltArrowDownIcon className="fill-neutral-900 stroke-neutral-900 stroke-1 duration-150" />
      )}
    </RadixSelectPrimitive.Icon>
  </RadixSelectPrimitive.Trigger>
);

const ScrollUpButton = ({
  className,
  ...props
}: ComponentProps<typeof RadixSelectPrimitive.ScrollUpButton>) => (
  <RadixSelectPrimitive.ScrollUpButton
    className={scrollButton({ className })}
    {...props}
  >
    <AltArrowUpIcon className="size-4" />
  </RadixSelectPrimitive.ScrollUpButton>
);

const ScrollDownButton = ({
  className,
  ...props
}: ComponentProps<typeof RadixSelectPrimitive.ScrollDownButton>) => (
  <RadixSelectPrimitive.ScrollDownButton
    className={scrollButton({ className })}
    {...props}
  >
    <AltArrowDownIcon className="size-4" />
  </RadixSelectPrimitive.ScrollDownButton>
);

const Content = ({
  className,
  children,
  position = "popper",
  ...props
}: ComponentProps<typeof RadixSelectPrimitive.Content>) => (
  <RadixSelectPrimitive.Portal>
    <RadixSelectPrimitive.Content
      className={content({ position, className })}
      position={position}
      {...props}
    >
      <ScrollUpButton />
      <RadixSelectPrimitive.Viewport className={viewport({ position })}>
        {children}
      </RadixSelectPrimitive.Viewport>
      <ScrollDownButton />
    </RadixSelectPrimitive.Content>
  </RadixSelectPrimitive.Portal>
);

const Item = ({
  className,
  children,
  ...props
}: ComponentProps<typeof RadixSelectPrimitive.Item>) => (
  <RadixSelectPrimitive.Item className={item({ className })} {...props}>
    <RadixSelectPrimitive.ItemText>{children}</RadixSelectPrimitive.ItemText>

    <RadixSelectPrimitive.ItemIndicator data-slot="item-indicator">
      <CheckIcon className="size-6 text-primary-950" />
    </RadixSelectPrimitive.ItemIndicator>
  </RadixSelectPrimitive.Item>
);

export const SelectPrimitive = {
  Root,
  Group,
  Value,
  Trigger,
  Content,
  Item,
  ScrollUpButton,
  ScrollDownButton,
};
